// src/pages/Login/Login.js
import React, { useState } from 'react'
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material'
import {
  VisibilityRounded,
  VisibilityOffRounded,
  LoginRounded
} from '@mui/icons-material'
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useAuth } from '../../context/AuthContext'

const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm()

  const onSubmit = async (data) => {
    setLoading(true)
    setError('')

    const result = await login(data)

    if (result.success) {
      const from = location.state?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    } else {
      setError(result.error)
    }

    setLoading(false)
  }

  return (
    <Container component='main' maxWidth='xs'>
      <Box
        sx={{
          mt: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 3,
            width: '100%',
            borderRadius: 2
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2
            }}
          >
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Box
                component='img'
                src='/logo_aux.png'
                alt='Umarkt'
                sx={{ width: 50, height: 50 }}
              />
            </Box>
            <Typography component='h1' variant='h5' fontWeight='bold'>
              Umarkt
            </Typography>
          </Box>

          <Typography variant='body1' textAlign='center' sx={{ mb: 1 }}>
            Entre para anunciar e gerenciar seus imóveis.
          </Typography>
          <Typography
            variant='body2'
            textAlign='center'
            color='text.secondary'
            sx={{ mb: 2 }}
          >
            Para visualizar anúncios e contatos, não é necessário fazer login.
          </Typography>

          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component='form' onSubmit={handleSubmit(onSubmit)}>
            <TextField
              margin='dense'
              required
              fullWidth
              size='small'
              label='Email'
              type='email'
              autoComplete='email'
              autoFocus
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register('email', {
                required: 'Email é obrigatório'
              })}
            />

            <TextField
              margin='dense'
              required
              fullWidth
              size='small'
              label='Senha'
              type={showPassword ? 'text' : 'password'}
              autoComplete='current-password'
              error={!!errors.password}
              helperText={errors.password?.message}
              {...register('password', {
                required: 'Senha é obrigatória',
                minLength: {
                  value: 6,
                  message: 'Senha deve ter pelo menos 6 caracteres'
                }
              })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      size='small'
                      onClick={() => setShowPassword(!showPassword)}
                      edge='end'
                    >
                      {showPassword ? (
                        <VisibilityOffRounded />
                      ) : (
                        <VisibilityRounded />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {/* Esqueci minha senha */}
            <Box sx={{ textAlign: 'right', mt: 0.5, mb: 1 }}>
              <Link
                component={RouterLink}
                to='/esqueci-senha'
                variant='body2'
                sx={{
                  textDecoration: 'none',
                  color: 'primary.main',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Esqueci minha senha
              </Link>
            </Box>

            <Button
              type='submit'
              fullWidth
              variant='contained'
              sx={{ mt: 1, mb: 2, py: 1 }}
              disabled={loading}
              startIcon={<LoginRounded />}
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                Não tem uma conta?{' '}
                <Link
                  component={RouterLink}
                  to='/registro'
                  sx={{
                    textDecoration: 'none',
                    fontWeight: 600,
                    color: 'primary.main'
                  }}
                >
                  Cadastre-se
                </Link>
              </Typography>

              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 1.5 }}
              >
                <Link
                  component={RouterLink}
                  to='/'
                  sx={{
                    textDecoration: 'none',
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  Voltar ao início
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  )
}

export default Login
